import React from "react";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import "./rghtnow-pages.css";
import "./legal-pages.css";

const intro = [
  "This privacy notice lets you know how we safeguard your personal data and the measures we take to keep it safe.",
  "Here, you can find out what kind of information we use to provide you with our services, how we use your information to keep you safe, and the choices and controls available to you.",
  "This notice also explains the possible rights you have regarding the privacy of your data.",
  "The term \"us\" or \"we\" refers to RGHTNOW. The term \"you\" or \"your\" refers to people who use our website, app, or services.",
];

const dataDefinitions = [
  ["Contact details", "Your personal details like name, phone number, and e-mail address to best help you."],
  ["Profile information", "Data used to build your profile, such as saved addresses, language preferences, and additional details required for some services."],
  ["Geolocation", "Your location may be used to offer local options, estimate time and distance, and improve destination or traffic guidance."],
  ["Payment details", "Information about the payment method used, such as the amount charged and card-related details."],
  ["In-app communication records", "Records of in-app chats and interactions used to support service quality and resolve disputes."],
  ["Limited identification data", "Device or IP information associated with app or website usage."],
  ["Ride or order data", "Usage information such as journey time, route, timestamps, order status, and service statistics."],
  ["User image", "Profile images may be stored for verification purposes where required."],
];

const cookieDefinitions = [
  ["Necessary / Essential Cookies", "Used to authenticate users, prevent fraud, help navigation, and provide requested services."],
  ["Cookies Policy / Notice Acceptance Cookies", "Used to identify whether a visitor has accepted cookie usage."],
  ["Functionality Cookies", "Used to remember preferences and provide a more personalized experience."],
];

const rightsDefinitions = [
  ["Your right of access", "You can ask for a copy of personal information collected about you, subject to lawful limits."],
  ["Your right to rectification", "You can ask us to update or complete records that you believe are inaccurate or incomplete."],
  ["Your right to erasure", "You can request removal of personal information stored on our systems in certain circumstances."],
  ["Your right to restriction of processing", "You can ask us to restrict processing of your information in certain circumstances."],
  ["Your right to object to processing", "You can object where data is processed based on legitimate interests and request a review."],
];

const sections = [
  {
    title: "Personal Data We Process",
    paragraphs: [
      "We only process information that is necessary for your needs on our marketplace. This helps us provide the best possible service.",
    ],
    definitions: dataDefinitions,
  },
  {
    title: "Usage Data",
    paragraphs: [
      "Usage data may be collected automatically and may include device, browser, and website activity information.",
    ],
    list: [
      "Device information such as IP address, device type, operating system, network state, and current location.",
      "Browser and website activity such as browser version, pages visited, and support interactions.",
    ],
  },
  {
    title: "Tracking Technologies and Cookies",
    paragraphs: [
      "We use cookies, beacons, tags, and scripts to track activity, store certain information, analyze data, improve performance, and provide a better user experience.",
      "Your browser may allow you to manage cookie acceptance. If cookies are disabled, some parts of the website may not function properly.",
    ],
    subtitle: "Types of Cookies Used:",
    definitions: cookieDefinitions,
  },
  {
    title: "Purpose of the Processing",
    paragraphs: [
      "We process your personal data to provide services efficiently and for operational purposes.",
    ],
    list: [
      "To connect users with nearby available services and support customer service or dispute resolution.",
      "To help track rides, orders, delivery routes, and service progress while the app or service is in use.",
      "To improve app performance, notify users about changes, and keep accounts safe.",
      "To process payments, maintain service standards, and resolve support or compliance issues.",
      "To keep you informed about journey, order, or service completion and related updates.",
    ],
  },
  {
    title: "Legal Basis for Processing",
    paragraphs: [
      "We use your data only when we have a proper legal reason for doing so, including contract, legitimate interest, legal obligation, or vital interests.",
      "Refusing to share necessary personal data, such as location information, may prevent us from providing some services.",
    ],
    list: [
      "Contract: data required to provide the personalized service you request.",
      "Legitimate interests: fraud prevention, system security, support, and service improvement.",
      "Legal obligation: processing required by valid legal requests or applicable law.",
      "Vital interests: processing required in emergencies or threats to life.",
    ],
  },
  {
    title: "Recipients (Who We Share Data With)",
    paragraphs: [
      "We only work with trusted partners and authorities, sharing only what is necessary. We do not sell your personal information.",
    ],
    list: [
      "Drivers, delivery partners, merchants, or service partners where needed to complete your request.",
      "Support teams for dispute handling, customer service, and service quality checks.",
      "External authorities where required by law, court order, or regulatory obligation.",
    ],
  },
  {
    title: "Security of Your Personal Data",
    paragraphs: [
      "Data security is a top priority and we use industry-accepted means of protection. No internet transmission or electronic storage method is 100% secure.",
    ],
  },
  {
    title: "Children's Privacy",
    paragraphs: [
      "We do not knowingly collect personal information from anyone under the age of 13. If a parent or guardian believes a child has provided personal data, they should contact us so it can be removed.",
    ],
  },
  {
    title: "Links to Other Websites & Changes to this Policy",
    paragraphs: [
      "Our website or app may link to third-party websites that are not operated by us. You should review the privacy policy of every site you visit.",
      "We may update this Privacy Policy from time to time and may notify users by email, website notice, or another appropriate method.",
    ],
  },
  {
    title: "Your Rights and Controls",
    paragraphs: [
      "We want you to remain in control of your personal data and provide controls to view information, manage choices, and handle cookie consent where available.",
    ],
    definitions: rightsDefinitions,
  },
  {
    title: "Retention and Payments",
    paragraphs: [
      "We keep personal data only as long as needed to provide services, comply with policies, meet legal obligations, resolve disputes, and maintain records.",
    ],
    list: [
      "Active account data may be stored while you use our services.",
      "Closed account data may be deleted according to retention schedules and legal requirements.",
      "Accounting, payment, or regulatory data may be retained for the required legal period.",
      "Credit/debit card details and personally identifiable information are not sold, shared, rented, or leased to third parties.",
      "Refunds are processed through the original mode of payment where applicable.",
    ],
  },
  {
    title: "Data Retention and Deletion",
    paragraphs: [
      "Users may request account deletion through the Contact Us page. RGHT Now may retain user data after a deletion request where legal, regulatory, safety, security, fraud prevention, unresolved claim, or dispute reasons require it.",
      "Data is generally deleted within 90 days of a deletion request, except where retention is necessary for the reasons above.",
      "Users can modify their account details through the profile edit section where available.",
    ],
  },
];

const PrivacyPolicyPage = () => (
  <div className="legal-page">
    <RghtnowMarketingHeader />
    <main className="legal-shell">
      <div className="container">
        <article className="privacy-card">
          <h1>RGHTNOW Privacy Notice</h1>
          <div className="privacy-note">
            {intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          {sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.subtitle && <h3>{section.subtitle}</h3>}
              {section.definitions && (
                <dl>
                  {section.definitions.map(([term, detail]) => (
                    <React.Fragment key={term}>
                      <dt>{term}</dt>
                      <dd>{detail}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
              {section.list && (
                <ul>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
          <section className="privacy-contact">
            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, you can contact us by email.</p>
            <p>
              <a href="mailto:admin@rghtnow.com">admin@rghtnow.com</a>
            </p>
          </section>
        </article>
      </div>
    </main>
    <a className="legal-whatsapp" href="https://wa.me/971504781269" target="_blank" rel="noreferrer" aria-label="WhatsApp">
      <i className="ph-fill ph-whatsapp-logo" />
    </a>
    <RghtnowMarketingFooter />
  </div>
);

export default PrivacyPolicyPage;
